import type { CompanyTimeSeries } from '../../../../hooks/useCompanyTimeSeries';
import {
  getYearArrayFromTimeseries,
  getEndDateByYear,
} from '../Sankey/utils/utils';
import { checkNonNilData, normalizedSafeGqlData } from '../Sankey/hooks/utils';
import type { CurrencyISO } from '@/constants/currencies';
import { toCurrency } from '@/utilities/formatting';
import { compact } from '@/utilities/fp';
import { useCallback } from 'react';
import { usePastTranslation } from '@/pages/CompanyReport/components/Past/hooks/usePastTranslation';

const FIRST_COLUMN_HEADER = '';

const ROW_TYPES = {
  FIXED: 'fixed',
  EXPENSES_SEGMENTS: 'expensesSegments',
  BUSINESS_SEGMENTS: 'businessSegments',
  GEO_SEGMENTS: 'geoSegments',
} as const;

const TABLE_ROWS = {
  revenue: ROW_TYPES.FIXED,
  costOfRevenue: ROW_TYPES.FIXED,
  grossProfit: ROW_TYPES.FIXED,
  expenses: ROW_TYPES.FIXED,
  dAExpense: ROW_TYPES.EXPENSES_SEGMENTS,
  generalAdministrative: ROW_TYPES.EXPENSES_SEGMENTS,
  nonOperatingExpense: ROW_TYPES.EXPENSES_SEGMENTS,
  rdExpense: ROW_TYPES.EXPENSES_SEGMENTS,
  salesMarketing: ROW_TYPES.EXPENSES_SEGMENTS,
  earnings: ROW_TYPES.FIXED,
} as const;

const keyAsValue = (obj: object) => {
  const result = Object.keys(obj).reduce((acc, key) => {
    acc[key] = key;
    return acc;
  }, {});
  return result;
};

export const getAllRevenueSegments = (rawGqlData: CompanyTimeSeries) => {
  const emptyData = {
    business: {},
    geographic: {},
  };
  const { timeSeries } = rawGqlData;
  return (
    timeSeries?.reduce(({ business, geographic }, item) => {
      const businessRevenueSegmentsLtm = item.businessRevenueSegmentsLtm || {};
      const geographicRevenueSegmentsLtm =
        item.geographicRevenueSegmentsLtm || {};

      const newBusiness = {
        ...business,
        ...keyAsValue(businessRevenueSegmentsLtm),
      };
      const newGeo = {
        ...geographic,
        ...keyAsValue(geographicRevenueSegmentsLtm),
      };
      return { business: newBusiness, geographic: newGeo };
    }, emptyData) || emptyData
  );
};

export const getTableHeaders = (rawGqlData?: CompanyTimeSeries) => {
  if (!rawGqlData) {
    return [];
  }
  const { timeSeries } = rawGqlData;
  const years = getYearArrayFromTimeseries(timeSeries);
  const yearHeaders = years.map((year) => year.toString());
  return [FIRST_COLUMN_HEADER, ...yearHeaders];
};

type RowType =
  | 'fixed'
  | 'expensesSegments'
  | 'businessSegments'
  | 'geoSegments';

type GetTableBodyRowParams = {
  rawGqlData: CompanyTimeSeries;
  rowKey: string;
  firstRowName: string;
  rowType: RowType;
};

const getTableBodyRow = ({
  rawGqlData,
  rowKey,
  firstRowName,
  rowType,
}: GetTableBodyRowParams) => {
  const { timeSeries } = rawGqlData;
  const years = getYearArrayFromTimeseries(timeSeries);
  const endDates = years.map((year) => getEndDateByYear(year));

  const yearValuesByKey = endDates.map((endDate) => {
    if (!checkNonNilData(rawGqlData)) {
      return '';
    }
    const data = normalizedSafeGqlData(rawGqlData, endDate);

    if (rowType === ROW_TYPES.EXPENSES_SEGMENTS) {
      return data.chosenTimeseries?.[rowKey] ?? '';
    }
    if (rowType === ROW_TYPES.BUSINESS_SEGMENTS) {
      return data.revenueSegments?.[rowKey] ?? '';
    }
    if (rowType === ROW_TYPES.GEO_SEGMENTS) {
      return data.geographicSegments?.[rowKey] ?? '';
    }

    return data[rowKey] / data.unit;
  });

  const compactedYearValuesByKey = compact(yearValuesByKey);

  if (compactedYearValuesByKey.length === 0) {
    return [];
  }

  return [firstRowName, ...yearValuesByKey];
};

export const useTableBody = () => {
  const t = usePastTranslation();
  return useCallback(
    (rawGqlData?: CompanyTimeSeries) => {
      if (!rawGqlData) {
        return [];
      }

      const fixedRows = Object.keys(TABLE_ROWS).map((rowKey) => {
        const firstRowName = t(
          `revenue.table.${rowKey as keyof typeof TABLE_ROWS}`
        );
        return getTableBodyRow({
          rawGqlData,
          rowKey,
          firstRowName,
          rowType: TABLE_ROWS[rowKey],
        });
      });

      const fixedRowsWithoutEmpty = fixedRows.filter((row) => row.length > 0);
      return fixedRowsWithoutEmpty;
    },
    [t]
  );
};

export const formatCurrencyForCellData = (
  value: string | number,
  currencyISO: CurrencyISO
) => {
  const cellDataInNumber = Number(value);
  if (Number.isNaN(cellDataInNumber)) {
    return value;
  }
  return toCurrency(cellDataInNumber, 0, currencyISO);
};
